import type { OrgRow } from '~/gql/organizations/organizations'
import { useMutation } from '@apollo/client/react'
import { OrganizationStatus } from '@zeus/index'
import { ArrowLeft, Loader, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_UPDATE_ORGANIZATION } from '~/gql/organizations/organizations'
import { client } from '~/helpers/apollo'

type IProps = {
  open: boolean
  onCancel: () => void
  onSuccess: () => void | Promise<unknown>
  org: OrgRow
}

function OrganizationEditModal({ open, onCancel, onSuccess, org }: IProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(org.name)
  const [slug, setSlug] = useState(org.slug)
  const [status, setStatus] = useState<OrganizationStatus>(org.status as OrganizationStatus)
  // Le renommage de slug casse toutes les URLs déjà en circulation : on l'isole
  // derrière une confirmation qui liste ce qui devient invalide (#155).
  const [confirmingRename, setConfirmingRename] = useState(false)

  const [updateOrganization, { loading }] = useMutation(GQL_UPDATE_ORGANIZATION, { client })

  const slugChanged = slug.trim() !== org.slug

  const onSubmit = async () => {
    try {
      await updateOrganization({
        variables: { input: { id: org.id, name: name.trim(), slug: slug.trim(), status } },
      })
      toast.success(t('organization.edit.success'))
      await onSuccess()
      onCancel()
    } catch {
      toast.error(t('organization.edit.failed'))
    }
  }

  /**
   * Un renommage passe d'abord par l'écran de confirmation ; le reste des champs
   * (nom, statut) s'enregistre directement. La validation de présence est faite
   * ici pour ne pas laisser entrer un slug vide dans la confirmation.
   */
  const handleSave = () => {
    if (!name.trim() || !slug.trim()) {
      toast.error(t('organization.edit.required'))
      return
    }
    if (slugChanged && !confirmingRename) {
      setConfirmingRename(true)
      return
    }
    void onSubmit()
  }

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent aria-describedby={undefined}>
        {confirmingRename
          ? (
              <>
                <DialogHeader>
                  <DialogTitle>{t('organization.rename.title')}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                    <TriangleAlert className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-500" />
                    <p>
                      {t('organization.rename.warning', { from: org.slug, to: slug.trim() })}
                    </p>
                  </div>
                  <ul className="ml-1 list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
                    <li>{t('organization.rename.break.urls')}</li>
                    <li>{t('organization.rename.break.login')}</li>
                    <li>{t('organization.rename.break.emails')}</li>
                  </ul>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setConfirmingRename(false)}>
                    <ArrowLeft className="mr-2 size-4" />
                    {t('common.back')}
                  </Button>
                  <Button variant="destructive" onClick={onSubmit} disabled={loading}>
                    {loading && <Loader className="mr-2 size-4 animate-spin" />}
                    {t('organization.rename.confirm')}
                  </Button>
                </DialogFooter>
              </>
            )
          : (
              <>
                <DialogHeader>
                  <DialogTitle>{t('organization.edit.title')}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="org-name">{t('common.name')}</Label>
                    <Input id="org-name" value={name} onChange={e => setName(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="org-slug">{t('organization.slug')}</Label>
                    <Input
                      id="org-slug"
                      value={slug}
                      onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                    />
                    <p className="text-xs text-muted-foreground">{t('organization.edit.slug.help')}</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="org-status">{t('common.status')}</Label>
                    <select
                      id="org-status"
                      value={status}
                      onChange={e => setStatus(e.target.value as OrganizationStatus)}
                      className="h-9 w-full rounded-md border border-border bg-transparent px-3 text-sm"
                    >
                      <option value={OrganizationStatus.ACTIVE}>{t('status.active')}</option>
                      <option value={OrganizationStatus.SUSPENDED}>{t('organization.suspended')}</option>
                    </select>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
                  <Button onClick={handleSave} disabled={loading}>
                    {loading && <Loader className="mr-2 size-4 animate-spin" />}
                    {t('common.save')}
                  </Button>
                </DialogFooter>
              </>
            )}
      </DialogContent>
    </Dialog>
  )
}

export default OrganizationEditModal
